import styled, { DefaultTheme, css } from 'styled-components'

export const Container = styled.div`
  ${() => css`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    &[data-layout='inline'] {
      display: flex;
      flex-direction: row;
      gap: 1rem;
    }

    &[data-layout='responsive-flex'] {
      display: flex;
      flex-direction: row;
      gap: 1rem;
      & > div {
        flex: 1;
      }
    }
    @media only screen and (max-width: 1200px) {
      &[data-layout='responsive-flex'] {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        & > div {
          flex: 1;
        }
      }
    }

    &[data-layout='grid'] {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    }
  `}
`

/**
 * Item
 */

type ItemProps = {
  size?: RadioItemSizes
  hasActiveChildren?: boolean
  isSelected?: boolean
  hasSelectedItem?: boolean
  withOpacityEffect?: boolean
}

const itemModifiers = {
  isSelected: () => css`
    box-shadow: 0px 4px 8px rgba(24, 21, 60, 0.08);
  `,
  activeChildren: () => css`
    label {
      margin-bottom: 0;
    }
  `,
  withOpacityEffect: (isSelected: boolean) =>
    isSelected ? 'opacity: 1;' : 'opacity: 0.7;',
  size: {
    // Deve virar um template
    tiny: () => css`
      --paddingY: 0rem;
      --paddingEnd: calc(var(--bulletWrapperSize) + 0.25rem + 0.5rem);
      --padding: 0 0 0 var(--paddingEnd);
      display: inline-flex;
      box-shadow: none !important;

      label {
        background: transparent !important;
        border: none !important;

        .label {
          font-size: 0.875rem;
          font-weight: normal;
        }
      }
    `,
    small: (theme: DefaultTheme) => css`
      --paddingY: ${theme.spacings.xxxsmall};
      --paddingStart: ${theme.spacings.small};
      --paddingEnd: calc(var(--bulletWrapperSize) + 0.5rem + 0.5rem);
      --padding: var(--paddingY) var(--paddingStart) var(--paddingY)
        var(--paddingEnd);
    `,
    medium: (theme: DefaultTheme) => css`
      --paddingY: ${theme.spacings.medium};
      --paddingStart: ${theme.spacings.small};
      --paddingEnd: calc(var(--bulletWrapperSize) + 0.5rem + 0.5rem);
      --padding: var(--paddingY) var(--paddingStart) var(--paddingY)
        var(--paddingEnd);
    `
  }
}

export const Item = styled.div<ItemProps>`
  ${({
    theme,
    hasActiveChildren,
    isSelected,
    size = 'medium',
    withOpacityEffect,
    hasSelectedItem
  }) => css`
    --borderColor: var(--neutralHighLight);
    --backgroundColor: ${theme.colors.neutral.high.lightest};

    --bulletRadius: ${theme.colors.primary};
    --backgroundBullet: ${theme.colors.white};

    --labelColor: var(--sl-fg-base);

    /* Controlando layers para o activeChildren  */
    position: relative;
    z-index: 2;
    box-shadow: 0;
    transition: all ${theme.transition.default};

    &:focus-visible {
      box-shadow: var(--sl-focus-ring-base);
      border: var(--sl-border-base-strong-hover);
    }

    &:hover:not(.disabled) {
      --borderColor: var(--hover);
    }

    &:active {
    }

    &.disabled {
      --bulletRadius: var(--neutralLowLightest);
      --backgroundBullet: var(--neutralHighLight);
      --labelColor: var(--sl-fg-base-disabled);

      cursor: not-allowed;

      * {
        cursor: not-allowed;
      }

      label {
        &:hover {
          color: inherit;
        }
      }
    }

    &.hide,
    input[type='radio'] {
      display: none;
    }

    &.selected {
    }

    /* Input */
    input[type='radio']:checked,
    &.selected input[type='radio'] {
      /* The .selected class is used in the radio list */
      + label {
        --borderColor: var(--primary);
        background-color: var(--backgroundColor);

        &:focus-visible {
          box-shadow:
            0rem 0rem 0rem 0.0625rem var(--sl-color-gray-0),
            0rem 0rem 0rem 0.1875rem
              color-mix(in srgb, var(--primary) 40%, transparent);
        }

        &:before {
          border: 6px solid var(--primary);
        }

        &:after {
          width: ${theme.spacings.xxsmall};
          height: ${theme.spacings.xxsmall};
        }
      }
    }

    /* Label */
    label {
      /* reset */
      margin-bottom: 0;
      /* reset */

      position: relative;

      display: flex;
      flex: auto;
      flex-wrap: wrap;

      border-radius: var(--sl-radius-2);
      padding: var(--padding);
      height: 100%;
      min-height: var(--bulletWrapperSize);
      border: 1px solid var(--borderColor);
      background-color: ${theme.colors.light};

      transition: all ${theme.transition.fast};
      cursor: pointer;

      color: var(--sl-fg-base-soft);

      .title {
        font-weight: 500;
      }

      .label_content {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        width: 100%;
      }

      .radio--copy {
        flex: 1;
        min-width: 0;
      }

      .radio--label-heading {
        display: flex;
        width: 100%;
        color: var(--labelColor);
      }

      &:before,
      &:after {
        content: '';
        position: absolute;
        top: calc(var(--paddingY) + 0.125rem);
        left: calc((var(--bulletWrapperSize) / 2) + 0.5rem);
        transform: translate(-50%, 0);
        border-radius: ${theme.border.radius.rounded};
      }

      &:before {
        width: var(--bulletWrapperSize);
        height: var(--bulletWrapperSize);
        border: var(--sl-border-base);

        background-color: var(--backgroundBullet);
      }

      aside {
        flex-shrink: 0;
        align-self: flex-start;

        &.radio__tooltip {
          display: flex;
          align-items: center;
          height: 20px;
        }
      }

      .radio--label-label {
        gap: 0.4rem;
      }
    }

    ${hasActiveChildren && isSelected && itemModifiers.activeChildren()}
    ${isSelected && itemModifiers.isSelected()}
    ${withOpacityEffect &&
    hasSelectedItem &&
    itemModifiers.withOpacityEffect(isSelected)}
    ${itemModifiers.size[size](theme)}
  `}
`

export const ActiveChildren = styled.div`
  ${({ theme }) => css`
    display: flex;
    margin-top: -${theme.spacings.medium};
    margin-bottom: 0.5rem; // Retornando MB default do label

    border-radius: ${theme.border.radius.medium};
    padding: 2rem ${theme.spacings.medium} ${theme.spacings.medium}
      ${theme.spacings.medium};

    border: 1px solid ${theme.colors.tertiary};
    background-color: ${theme.colors.white};

    transition: all ${theme.transition.fast};
  `}
`
